(() => {
  class PLAdventCarousel {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-stage]');
      this.track = root.querySelector('[data-track]');
      this.slides = [...root.querySelectorAll('[data-slide]')];
      this.prev = root.querySelector('[data-prev]');
      this.next = root.querySelector('[data-next]');
      this.surprise = root.querySelector('[data-surprise]');
      this.current = root.querySelector('[data-current]');
      this.progressFill = root.querySelector('[data-progress-fill]');
      this.numbers = [...root.querySelectorAll('[data-number]')];
      this.revealButtons = [...root.querySelectorAll('[data-reveal]')];
      this.storageKey = root.dataset.revealStorage || 'pl-advent-reveals';
      this.revealed = this.loadRevealed();

      this.modal = root.querySelector('[data-modal]');
      this.dialog = root.querySelector('[data-dialog]');
      this.modalBody = root.querySelector('[data-modal-body]');
      this.modalPrev = root.querySelector('[data-modal-prev]');
      this.modalNext = root.querySelector('[data-modal-next]');
      this.closeButtons = [...root.querySelectorAll('[data-close-modal]')];

      this.active = 0;
      this.modalIndex = 0;
      this.startX = null;
      this.startY = null;
      this.lastFocus = null;

      if (!this.slides.length) return;
      this.bind();
      this.render(false);

      /* Product titles are editable and may wrap to multiple lines.
         Re-measure after fonts have loaded so the stage never clips a card. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.syncTrackHeight());
      }

      window.setTimeout(() => this.syncTrackHeight(), 120);
    }

    wrap(i) {
      return (i + this.slides.length) % this.slides.length;
    }

    circularOffset(index) {
      let offset = index - this.active;
      const len = this.slides.length;
      if (offset > len / 2) offset -= len;
      if (offset < -len / 2) offset += len;
      return offset;
    }

    bind() {
      this.prev?.addEventListener('click', () => this.select(this.active - 1, true));
      this.next?.addEventListener('click', () => this.select(this.active + 1, true));

      this.revealButtons.forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          this.reveal(Number(button.dataset.index));
        });
      });

      this.surprise?.addEventListener('click', () => {
        if (this.slides.length < 2) return;

        const unrevealed = this.slides
          .map((_, index) => index)
          .filter(index => !this.revealed.has(index));

        let pool = unrevealed.length ? unrevealed : this.slides.map((_, index) => index);
        pool = pool.filter(index => index !== this.active);

        if (!pool.length) return;

        const i = pool[Math.floor(Math.random() * pool.length)];
        this.select(i, true);
      });

      this.numbers.forEach(button => {
        button.addEventListener('click', () => this.select(Number(button.dataset.index), true));
      });

      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', event => {
          const open = event.target.closest('[data-open-modal]');
          if (open) {
            event.preventDefault();

            if (!this.revealed.has(index)) {
              this.select(index, true);
              return;
            }

            if (index !== this.active) {
              this.select(index, true);
              return;
            }

            this.openModal(index, open);
            return;
          }

          if (index !== this.active) this.select(index, true);
        });
      });

      this.track?.addEventListener('pointerdown', e => this.pointerDown(e));
      this.track?.addEventListener('pointerup', e => this.pointerUp(e));
      this.track?.addEventListener('pointercancel', () => this.clearPointer());

      this.stage?.addEventListener('keydown', e => {
        if (!this.modal || !this.modal.hidden) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.select(this.active - 1, true); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.select(this.active + 1, true); }
      });

      this.modalPrev?.addEventListener('click', () => this.changeModal(-1));
      this.modalNext?.addEventListener('click', () => this.changeModal(1));
      this.closeButtons.forEach(button => button.addEventListener('click', () => this.closeModal()));

      document.addEventListener('keydown', e => this.onKeydown(e));
      window.addEventListener('resize', () => {
        this.render(false);
        window.setTimeout(() => this.syncTrackHeight(), 40);
      });

      document.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('[data-slide]');
        if (!slide || !this.root.contains(slide)) return;
        this.select(Number(slide.dataset.index), true);
      });
    }

    loadRevealed() {
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        const values = raw ? JSON.parse(raw) : [];
        return new Set(
          Array.isArray(values)
            ? values.filter(value => Number.isInteger(value) && value >= 0 && value < this.slides.length)
            : []
        );
      } catch (_) {
        return new Set();
      }
    }

    saveRevealed() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify([...this.revealed]));
      } catch (_) {}
    }

    reveal(index) {
      const safeIndex = this.wrap(index);

      if (safeIndex !== this.active) {
        this.select(safeIndex, true);
      }

      if (this.revealed.has(safeIndex)) return;

      this.revealed.add(safeIndex);
      this.saveRevealed();

      const slide = this.slides[safeIndex];
      slide?.classList.add('is-revealed');

      requestAnimationFrame(() => {
        this.syncTrackHeight();

        const discover = slide?.querySelector('[data-open-modal]');
        if (discover) {
          window.setTimeout(() => discover.focus({ preventScroll: true }), 420);
        }
      });
    }

    select(index, scrollNumber = true) {
      this.active = this.wrap(index);
      this.render(scrollNumber);
    }

    render(scrollNumber = true) {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const shift = mobile ? 94 : 78;
      const depth = mobile ? -50 : -100;
      const rotate = mobile ? -5 : -9;

      this.slides.forEach((slide, index) => {
        const offset = this.circularOffset(index);
        const abs = Math.abs(offset);
        const scale = Math.max(.69, 1 - abs * (mobile ? .12 : .1));
        const visible = abs <= 2;

        slide.classList.toggle('is-active', offset === 0);
        slide.classList.toggle('is-revealed', this.revealed.has(index));
        slide.setAttribute('aria-hidden', offset === 0 ? 'false' : 'true');
        slide.style.zIndex = String(Math.max(1, 10 - Math.round(abs)));
        slide.style.opacity = !visible ? '0' : abs === 0 ? '1' : abs === 1 ? '.5' : '.14';
        slide.style.pointerEvents = abs <= 1 ? 'auto' : 'none';
        slide.style.transform =
          `translateX(calc(-50% + ${offset * shift}%)) translateZ(${abs * depth}px) rotateY(${offset * rotate}deg) scale(${scale})`;
      });

      if (this.current) this.current.textContent = String(this.active + 1).padStart(2, '0');
      if (this.progressFill) this.progressFill.style.width = `${((this.active + 1) / this.slides.length) * 100}%`;

      this.numbers.forEach((button, index) => {
        const active = index === this.active;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (scrollNumber && this.numbers[this.active]) {
        this.numbers[this.active].scrollIntoView({
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }

      requestAnimationFrame(() => this.syncTrackHeight());
    }

    syncTrackHeight() {
      if (!this.track || !this.slides.length) return;

      /* offsetHeight is not affected by the 3D transform, so we can safely
         measure all populated cards and give the stage enough room for the tallest. */
      const tallest = this.slides.reduce((max, slide) => {
        return Math.max(max, slide.offsetHeight || 0);
      }, 0);

      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const fallback = mobile ? 470 : 520;
      const breathingRoom = mobile ? 12 : 18;
      const desired = Math.max(fallback, tallest + breathingRoom);

      this.track.style.height = `${desired}px`;
    }

    pointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      if (e.target.closest('button')) return;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.track?.classList.add('is-dragging');
      try { this.track?.setPointerCapture(e.pointerId); } catch (_) {}
    }

    pointerUp(e) {
      if (this.startX === null || this.startY === null) return;
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy) * 1.15) {
        this.select(this.active + (dx < 0 ? 1 : -1), true);
      }
      this.clearPointer();
      try { this.track?.releasePointerCapture(e.pointerId); } catch (_) {}
    }

    clearPointer() {
      this.startX = null;
      this.startY = null;
      this.track?.classList.remove('is-dragging');
    }

    openModal(index, trigger) {
      if (!this.modal || !this.dialog || !this.modalBody) return;
      this.modalIndex = this.wrap(index);
      this.active = this.modalIndex;
      this.render(true);
      this.lastFocus = trigger || document.activeElement;
      this.populateModal();
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pl-advent-carousel-modal-open');
      requestAnimationFrame(() => this.dialog.focus());
    }

    populateModal() {
      const template = this.slides[this.modalIndex]?.querySelector('[data-modal-template]');
      if (!template) return;
      this.modalBody.innerHTML = template.innerHTML;
      this.dialog?.scrollTo({ top: 0, behavior: 'auto' });
    }

    changeModal(direction) {
      this.modalIndex = this.wrap(this.modalIndex + direction);
      this.active = this.modalIndex;
      this.render(true);
      this.populateModal();
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pl-advent-carousel-modal-open');
      this.lastFocus?.focus?.();
      this.lastFocus = null;
    }

    onKeydown(e) {
      if (!this.modal || this.modal.hidden || !this.root.isConnected) return;
      if (e.key === 'Escape') { e.preventDefault(); this.closeModal(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.changeModal(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.changeModal(1); }
      else if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      const focusable = [...this.dialog.querySelectorAll(
        'button:not([disabled]), a[href], summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter(el => el.offsetParent !== null);

      if (!focusable.length) {
        e.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-pl-advent-carousel]').forEach(root => {
      if (root.dataset.initialised === 'true') return;
      root.dataset.initialised = 'true';
      new PLAdventCarousel(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', e => init(e.target));
})();
